import React, { useEffect, useRef, useState } from 'react';

import { Card } from 'primereact/card';
import { DataView } from 'primereact/dataview';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';

import { getPriorityStyle } from './helpers';

interface TaskMobileViewProps {
  tasks: any[];
  loading?: boolean;
  onTaskClick?: (task: any) => void;
  header?: React.ReactNode;
  first?: number;
  rows?: number;
  totalRecords?: number;
  rowsPerPageOptions?: number[];
  onPage?: (event: any) => void;
  paginatorTemplate?: string;
  currentPageReportTemplate?: string;
  paginatorLeft?: React.ReactNode;
  paginatorRight?: React.ReactNode;
  isMobile?: boolean;
  scrollHeight?: string;
  selectedTaskId?: number | string | null;
}

const TaskMobileView: React.FC<TaskMobileViewProps> = ({
  tasks,
  loading = false,
  onTaskClick,
  header,
  first = 0,
  rows = 100,
  totalRecords = 0,
  rowsPerPageOptions = [10, 25, 50, 100],
  onPage,
  paginatorTemplate = 'RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink',
  currentPageReportTemplate = '{currentPage} из {totalPages}',
  paginatorLeft,
  paginatorRight,
  selectedTaskId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  // Вычитаем высоту хедера (64px) + поиск и кнопки (150px) + пагинатор (60px) + отступы
  const FIXED_OFFSET = 64 + 127; // 294px
  const scrollHeight = `calc(100dvh - ${FIXED_OFFSET}px)`;

  useEffect(() => {
    const applyStyles = () => {
      if (containerRef.current) {
        containerRef.current.style.maxHeight = scrollHeight;
        containerRef.current.style.overflow = 'auto';
        setIsReady(true);
      }
    };

    applyStyles();
    const timeoutId = setTimeout(applyStyles, 100);

    const resizeObserver = new ResizeObserver(() => {
      applyStyles();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [scrollHeight]);

  useEffect(() => {
    if (!loading && tasks.length > 0) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.style.maxHeight = scrollHeight;
          containerRef.current.style.overflow = 'auto';
        }
      });
    }
  }, [loading, tasks.length, scrollHeight]);

  const itemTemplate = (task: any) => {
    const isSelected = selectedTaskId === task.id;

    return (
      <Card 
        className={`mb-3 w-full ${isSelected ? 'selected-task-card' : ''}`}
        style={{ 
          cursor: 'pointer',
          width: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          ...(isSelected && {
            backgroundColor: '#EFF6FF'
          })
        }}
        onClick={() => onTaskClick?.(task)}
      >
        <div className="flex flex-column gap-2">
          <div className="flex justify-content-between align-items-center">
            <span className="text-lg">
              <strong>№{task.id} </strong>
              от
              <strong> {task.creationDate}</strong> 
            </span>
          </div>

          <div className="flex flex-column gap-1">
            <span className="font-bold text-lg">
              {task.name || ''}
            </span>
          </div>
          <div className="grid gap-1" style={{ marginTop: '4px' }}>
            {task.taskStatus && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Тип:</span>
                <span className="text-sm">{task.taskType}</span>
              </div>
            )}
            {task.taskStatus && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Статус:</span>
                <span className="text-sm">{task.taskStatus}</span>
              </div>
            )}

            {task.assetName && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Объект:</span>
                <span className="text-color-secondary text-sm">{task.assetName}</span>
              </div>
            )}

            {task.departmentName && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Участок:</span>
                <span className="text-color-secondary text-sm">{task.departmentName}</span>
              </div>
            )}

            {task.executors && task.executors.length > 0 && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Исполнители:</span>
                <span className="text-color-secondary text-sm">
                  {task.executors.map((e: any) => e.fullName || e.name || e.label).join(', ')}
                </span>
              </div>
            )}
            {task.priority && task.priority.length > 0 && (
              <div className="flex align-items-center gap-2 col-12">
                <span className="text-sm font-medium" style={{ minWidth: '70px' }}>Приоритет:</span>
                <span className="text-sm" style={getPriorityStyle(task.priority)}>
                  {task.priority === 'Высокий' && (
                    <i
                      className="pi pi-exclamation-triangle"
                      style={{ marginRight: '5px' }}
                    ></i>
                  )}
                  {task.priority}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-content-center py-6">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="task-mobile-view"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '0 8px',
        ...(isReady && { maxHeight: scrollHeight, overflow: 'auto' })
      }}
    >
      <div style={{ flex: 1, overflow: 'auto' }}>
        <DataView
          value={tasks}
          itemTemplate={itemTemplate}
          emptyMessage="Нет нарядов"
          layout="list"
        />
      </div>
      <div style={{ flexShrink: 0 }}>
        <Paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={onPage}
          template={paginatorTemplate}
          currentPageReportTemplate={currentPageReportTemplate}
          leftContent={paginatorLeft}
          rightContent={paginatorRight}
        />
      </div>
    </div>
  );
};

export default TaskMobileView;